// ContactDialog.jsx
import React from 'react'
import PropTypes from 'prop-types'
import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Typography,
  Box,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation } from '@apollo/client/react'
import { CANCEL_DEALSAUCE } from '@/housefolios-components/zComponents/mutations'
import UnifiedSubscribeDialog from '@/housefolios-components/Session/UnifiedSubscribeDialog'
import withSession from '@/housefolios-components/Session/withSession'

const ContactDialog = ({
  open,
  handleClose,
  affiliate = '',
  activeOrg = null,
  dealsauceStripeCustomerID = null,
  goHighLevelId = null,
  session,
  hidePlanActions = false,
}) => {
  let memberValue = ''
  if (affiliate) memberValue = affiliate
  if (activeOrg) memberValue = activeOrg
  const normalizedMember = String(memberValue || '').toLowerCase()
  const isPepMember = normalizedMember === 'pep'

  const [cancelDealsauce] = useMutation(CANCEL_DEALSAUCE)
  const [showCancelForm, setShowCancelForm] = React.useState(false)
  const [cancelReason, setCancelReason] = React.useState('')
  const [cancelComment, setCancelComment] = React.useState('')
  const [cancelSubmitting, setCancelSubmitting] =
    React.useState(false)
  const [cancelError, setCancelError] = React.useState('')

  const orgId =
    session?.me?.organization ||
    session?.me?.activeOrg?._id ||
    session?.me?.activeOrgId ||
    null

  const dealsauceCustomerId =
    dealsauceStripeCustomerID ||
    session?.me?.activeOrg?.dealsauceStripeCustomerID ||
    null
  const ghlId =
    goHighLevelId || session?.me?.activeOrg?.goHighLevelId || null
  const host =
    typeof window !== 'undefined' ? window.location.host : ''
  const isDealsauceDomain = String(host).includes('dealsauce')
  const isDealsauceMember = ['pacemorby', 'astroflip'].includes(
    normalizedMember,
  )
  const isRealeflowOrg = Boolean(session?.me?.activeOrg?.isRealeflow)
  // Realeflow orgs are managed via DealSauce support + Stripe tooling.
  // Show plan actions for Realeflow orgs (and for DealSauce-domain/member accounts).
  const showPlanActions = Boolean(
    !hidePlanActions &&
    (isRealeflowOrg || isDealsauceDomain || isDealsauceMember),
  )
  const showDealsauceSupportForm = Boolean(
    !isPepMember &&
    (isRealeflowOrg || isDealsauceDomain || isDealsauceMember),
  )

  const onCancelClick = async () => {
    setCancelError('')
    setShowCancelForm(true)
  }

  const [switchPlanOpen, setSwitchPlanOpen] = React.useState(false)

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Contact Support</DialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          align="center"
          style={{
            maxWidth: 720,
            margin: '24px auto 24px',
            color: '#555',
            lineHeight: 1.6,
          }}
        >
          {showDealsauceSupportForm
            ? 'Submit a support ticket using the link below (opens Microsoft Forms in a new tab). For account or membership questions, our team will follow up by email. Please also use the training videos and live training webinars for tips on using the platform.'
            : 'For questions about the status of your account or membership, please reach out to our support team. Please refer to the training videos and live training webinars for tips and tricks on using the platform.'}
        </Typography>
        {showDealsauceSupportForm ? (
          <>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                mt: 1,
                mb: 2,
              }}
            >
              <Button
                variant="contained"
               
                component="a"
                href="https://wkf.ms/46LsDZT"
                target="_blank"
                rel="noopener noreferrer"
                size="large"
               sx={{
                 boxShadow: "0 0.25rem 0.55rem rgba(244, 119, 46, 0.35)",
                 color: "#fff",
                 backgroundColor: "#f4772e",
                 backgroundImage: "none",
                 borderColor: "#f4772e",
                 "&:not(:disabled):hover": { backgroundColor: "#f4772e", color: "#fff" },
                 "&:not(:disabled).active, &:not(:disabled).active:hover, &:not(:disabled):active": { backgroundColor: "#e35b0c" },
                 "&:not(:disabled):active": { boxShadow: "0 0.22rem 0.525rem rgba(244, 119, 46, 0.4), 0 0.0625rem 0.385rem rgba(244, 119, 46, 0.54)" },
                 "&:not(:disabled).active .btn-wrapper--icon svg path, &:hover .btn-wrapper--icon svg path": { fill: "#fff" },
               }}>
                Submit a support ticket
              </Button>
            </Box>
            <Typography
              variant="body2"
              align="center"
              sx={{
                color: 'text.secondary',
                mb: 1,
              }}
            >
              You can also reach us at{' '}
              <strong>help@dealsauce.io</strong>.
            </Typography>
          </>
        ) : (
          <Grid
            container
            direction="row"
            sx={{
              justifyContent: isPepMember ? 'space-around' : 'center',
              alignItems: 'center',
            }}
          >
            {isPepMember && (
              <>
                <Grid size={{ xs: 12, sm: 5 }}>
                  <Box sx={{ textAlign: 'center', p: '3rem' }}>
                    <Box sx={[{ borderRadius: '50%', m: 0 }, {
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: "0.25rem",
                      position: "relative",
                      fontSize: "1.2rem",
                      transition: "all 0.2s ease-in-out",
                      "&:last-child": { mr: 0, marginRight: 0 },
                      "& .badge": { position: "absolute", right: 0, bottom: 0 },
                      "& svg, & .svg-inline--fa": { display: "block" },
                      marginRight: "0.25rem",
                    }]}>
                      <Box sx={[{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: 0,
                        backgroundColor: "rgb(100%, 98.5067873303%, 94.7058823529%) !important",
                        height: "130px !important",
                        width: "130px !important",
                        lineHeight: "130px !important",
                        p: 0,
                        borderRadius: "50%",
                        color: "#f4772e",
                        m: 0,
                      }, {
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: "0.25rem",
                        position: "relative",
                        fontSize: "1.2rem",
                        transition: "all 0.2s ease-in-out",
                        "&:last-child": { mr: 0, marginRight: 0 },
                        "& .badge": { position: "absolute", right: 0, bottom: 0 },
                        "& svg, & .svg-inline--fa": { display: "block" },
                        marginRight: "0.25rem",
                      }]}>
                        <FontAwesomeIcon icon={['fas', 'phone']} style={{ display: 'flex', alignSelf: 'center', }} sx={{ fontSize: "2.5rem", fontWeight: "300", lineHeight: "1.2" }} />
                      </Box>
                    </Box>
                    <Box
                      component="h4"

                      style={{ marginLeft: 20 }}
                      sx={{ fontWeight: 700, mt: '1.5rem' }}
                    >
                      (725) 331-1001
                    </Box>
                  </Box>
                </Grid>
                <Divider orientation="vertical" flexItem />
                          </>
            )}
            <Grid size={{ xs: 12, sm: 5 }}>
              <Box sx={{ textAlign: 'center', p: '3rem' }}>
                <Box sx={[{ borderRadius: '50%', m: 0 }, {
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: "0.25rem",
                  position: "relative",
                  fontSize: "1.2rem",
                  transition: "all 0.2s ease-in-out",
                  "&:last-child": { mr: 0, marginRight: 0 },
                  "& .badge": { position: "absolute", right: 0, bottom: 0 },
                  "& svg, & .svg-inline--fa": { display: "block" },
                  marginRight: "0.25rem",
                }]}>
                  <Box sx={[{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: 0,
                    backgroundColor: "rgb(100%, 98.5067873303%, 94.7058823529%) !important",
                    height: "130px !important",
                    width: "130px !important",
                    lineHeight: "130px !important",
                    p: 0,
                    borderRadius: "50%",
                    color: "#f4772e",
                    m: 0,
                  }, {
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: "0.25rem",
                    position: "relative",
                    fontSize: "1.2rem",
                    transition: "all 0.2s ease-in-out",
                    "&:last-child": { mr: 0, marginRight: 0 },
                    "& .badge": { position: "absolute", right: 0, bottom: 0 },
                    "& svg, & .svg-inline--fa": { display: "block" },
                    marginRight: "0.25rem",
                  }]}>
                    <FontAwesomeIcon icon={['far', 'envelope']} style={{ display: 'flex', alignSelf: 'center' }} sx={{ fontSize: "2.5rem", fontWeight: "300", lineHeight: "1.2" }} />
                  </Box>
                </Box>
                <Box
                  component="h4"

                  style={{ marginLeft: -30 }}
                  sx={{ fontWeight: 700, mt: '1.5rem' }}
                >
                  {isPepMember
                    ? 'support@propertyedge.pro'
                    : 'help@dealsauce.io'}
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}
        {showPlanActions && !showCancelForm && (
          <Grid
            container
            direction="row"
            style={{ marginTop: 20 }}
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Grid>
              <Button
                variant="contained"
               
                onClick={() => setSwitchPlanOpen(true)}
                startIcon={
                  <FontAwesomeIcon icon={['far', 'times']} sx={{ fontSize: "1rem !important" }} />
                }
              >
                Switch Plan
              </Button>
            </Grid>
          </Grid>
        )}
        {showPlanActions && !showCancelForm && (
          <Grid
            container
            direction="row"
            style={{ marginTop: 20 }}
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Grid>
              <Button
                variant="contained"
               
                onClick={onCancelClick}
                startIcon={
                  <FontAwesomeIcon icon={['far', 'times']} sx={[{
                    boxShadow: "0 0.25rem 0.55rem rgba(248, 50, 69, 0.35)",
                    color: "#fff",
                    backgroundColor: "#f83245",
                    backgroundImage: "none",
                    borderColor: "#f83245",
                    "&:not(:disabled):hover": { backgroundColor: "#f83245", color: "#fff" },
                    "&:not(:disabled).active, &:not(:disabled).active:hover, &:not(:disabled):active": { backgroundColor: "#ef081e" },
                    "&:not(:disabled):active": { boxShadow: "0 0.22rem 0.525rem rgba(248, 50, 69, 0.4), 0 0.0625rem 0.385rem rgba(248, 50, 69, 0.54)" },
                    "&:not(:disabled).active .btn-wrapper--icon svg path, &:hover .btn-wrapper--icon svg path": { fill: "#fff" },
                  }, { fontSize: "1rem !important" }]}/>
                }
              >
                Cancel Account
              </Button>
            </Grid>
          </Grid>
        )}

        {showPlanActions && showCancelForm && (
          <Grid
            container
            direction="column"
            style={{ marginTop: 20 }}
            spacing={2}
            sx={{
              justifyContent: 'center',
              alignItems: 'stretch',
            }}
          >
            <Grid>
              <div
                style={{
                  textAlign: 'center',
                  fontWeight: 700,
                  fontSize: 22,
                }}
              >
                We're sad to see you go...
              </div>
              <div
                style={{
                  textAlign: 'center',
                  color: '#6b6b6b',
                  marginTop: 8,
                }}
              >
                Before you cancel, please let us know the reason you
                are leaving.
              </div>
            </Grid>
            <Grid>
              <div style={{ display: 'grid', gap: 8 }}>
                {[
                  'Does not meet my needs',
                  'App is not performing well',
                  "I don't need it anymore",
                  'I found something better',
                  "It's too expensive",
                  'Other',
                ].map((label) => (
                  <label
                    key={label}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    <input
                      type="radio"
                      name="ds-cancel-reason"
                      value={label}
                      onChange={(e) =>
                        setCancelReason(e.target.value)
                      }
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </Grid>
            <Grid>
              <textarea
                placeholder="Anything you want to share? (Optional)"
                style={{
                  width: '100%',
                  minHeight: 100,
                  padding: 10,
                  borderRadius: 6,
                  border: '1px solid #e0e0e0',
                }}
                onChange={(e) => setCancelComment(e.target.value)}
              />
            </Grid>
            <Grid style={{ textAlign: 'center' }}>
              <Button
                variant="contained"
               
                onClick={async () => {
                  setCancelError('')
                  setCancelSubmitting(true)
                  try {
                    // Single path: cancel Stripe and notify GHL from backend
                    await cancelDealsauce({
                      variables: {
                        orgId,
                        reason: cancelReason || 'Other',
                        comment: cancelComment || '',
                      },
                    })
                    if (handleClose) handleClose()
                  } catch (e) {
                    const msg =
                      e?.graphQLErrors?.[0]?.message ||
                      e?.message ||
                      'Cancellation failed. Please contact support.'
                    setCancelError(
                      String(msg)
                        .replace(/^GraphQL error:\s*/i, '')
                        .replace(/^Error:\s*/i, ''),
                    )
                  } finally {
                    setCancelSubmitting(false)
                  }
                }}
                disabled={!cancelReason || cancelSubmitting}
              >
                {cancelSubmitting
                  ? 'Cancelling…'
                  : 'Confirm Cancellation'}
              </Button>
            </Grid>
            {Boolean(cancelError) && (
              <Grid>
                <Typography
                  role="alert"
                  style={{
                    color: '#d32f2f',
                    fontSize: 13,
                    textAlign: 'center',
                  }}
                >
                  {cancelError}
                </Typography>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>

      {!isPepMember && (
        <UnifiedSubscribeDialog
          open={switchPlanOpen}
          onClose={() => setSwitchPlanOpen(false)}
          session={session}
        />
      )}
    </Dialog>
  )
}

// Define PropTypes for type checking
ContactDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  affiliate: PropTypes.string,
  activeOrg: PropTypes.string,
  dealsauceStripeCustomerID: PropTypes.string,
  goHighLevelId: PropTypes.string,
  session: PropTypes.object,
  hidePlanActions: PropTypes.bool,
}

export default ContactDialog
